// import React, { } from 'react';
import { X } from 'lucide-react';
import {
  DeviceDashboardDto,
  DeviceOffsetsData,
  UpdateDeviceOffsetsDataRequest,
} from '@/services/deviceApi';
import '../../styles/pages/DeviceOffsets.css';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { DevicesContext } from '../../contexts/DevicesContext';
import SuccessModal from '../../components/modals/SuccessModal';

interface DeviceOffSetsProps {
  deviceDetails: DeviceDashboardDto;
  isOpen: boolean;
  onClose: () => void;
}

function DeviceOffSets({
  deviceDetails,
  isOpen,
  onClose,
}: DeviceOffSetsProps) {
  const [offsetsData, setOffsetsData] = useState<DeviceOffsetsData[]>([]);
  const [offsetErrors, setOffsetErrors] = useState<
    Record<number, { offsetX?: boolean; offsetY?: boolean }>
  >({});

  // Transient warning shown when the user tries to type a value past the
  // per-field cap. Auto-clears shortly after the last attempt.
  const [maxWarning, setMaxWarning] = useState<{
    offsetX?: boolean;
    offsetY?: boolean;
  }>({});
  const maxWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashMaxWarning = (field: 'offsetX' | 'offsetY') => {
    setMaxWarning(prev => ({ ...prev, [field]: true }));
    if (maxWarningTimerRef.current) clearTimeout(maxWarningTimerRef.current);
    maxWarningTimerRef.current = setTimeout(() => setMaxWarning({}), 2500);
  };
  useEffect(
    () => () => {
      if (maxWarningTimerRef.current) clearTimeout(maxWarningTimerRef.current);
    },
    []
  );

  /* success modal stats */
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successAction, setSuccessAction] = useState("");
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>("success");

  // context to updat data
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const { updateDeviceOffsetsData, deviceOffsetsData } = devicesContext;

  // Per-field caps: X-Offset is bounded to 10, Y-Offset to 5000.
  const OFFSET_X_MAX = 10;
  const OFFSET_Y_MAX = 5000;
  const getOffsetMax = (name: string) =>
    name === 'offsetX' ? OFFSET_X_MAX : OFFSET_Y_MAX;

  // A "complete" number is empty (treated as 0) or matches a real decimal
  // pattern and is no greater than the field's max. Bare "." or "..",
  // trailing-dot like "5.", partial entries, and out-of-range values are rejected.
  const isCompleteValidNumber = (value: string | number, max: number) => {
    const v = String(value ?? '');
    if (v === '') return false;
    if (!/^(\d+(\.\d+)?|\.\d+)$/.test(v)) return false;
    return parseFloat(v) <= max;
  };

  const handleSubmit = async () => {
    // Validate every field before submitting.
    const newErrors: Record<number, { offsetX?: boolean; offsetY?: boolean }> = {};
    let hasError = false;
    offsetsData.forEach(d => {
      const xValid = isCompleteValidNumber(d.offsetX, OFFSET_X_MAX);
      const yValid = isCompleteValidNumber(d.offsetY, OFFSET_Y_MAX);
      if (!xValid || !yValid) {
        newErrors[d.parameterId] = {
          ...(xValid ? {} : { offsetX: true }),
          ...(yValid ? {} : { offsetY: true }),
        };
        hasError = true;
      }
    });
    setOffsetErrors(newErrors);
    if (hasError) return;

    const deviceOffsetsData: UpdateDeviceOffsetsDataRequest[] = offsetsData.map(
      d => ({
        parameterId: d.parameterId,
        offsetX: d.offsetX === '' ? 0 : Number(d.offsetX),
        offsetY: d.offsetY === '' ? 0 : Number(d.offsetY),
      })
    );

    console.log(deviceOffsetsData);
    const result = await updateDeviceOffsetsData(deviceDetails.deviceId.toString(), deviceOffsetsData);
    if (result) {
      setShowSuccess(true);
      setSuccessMessage("Device Offsets updated");
      setSuccessStatus("success");
      setSuccessAction("Redirecting to device details page...");

      setTimeout(() => {
        onClose();
      }, 1200);
    }else {
      setShowSuccess(true);
      setSuccessMessage("Device Offsets updated failed");
      setSuccessStatus("failed");
      setSuccessAction("Please try again...");
    }
  };

  useEffect(() => {
    if (deviceOffsetsData?.length > 0) {
      const formattedData = deviceOffsetsData.map(d => ({
        ...d,
        offsetX: typeof d.offsetX === 'number' ? d.offsetX.toFixed(1) : d.offsetX,
        offsetY: typeof d.offsetY === 'number' ? d.offsetY.toFixed(1) : d.offsetY,
      }));
      setOffsetsData(formattedData);
    } else {
      const OffsetsData = Object.values(deviceDetails.parameters).map(p => ({
        parameterName: p.parameterName,
        parameterId: p.parameterDriverId,
        offsetX: (1.0).toFixed(1),
        offsetY: (0.0).toFixed(1),
      }));
      setOffsetsData(OffsetsData);
    }
  }, []);

  // Allow only non-negative decimals up to 10 characters and a value within the
  // per-field cap. Rejects letters, symbols, multiple dots, signs, and values
  // beyond the max for that field.
  const isValidOffsetInput = (value: string, max: number) => {
    if (value.length > 10 || !/^\d*\.?\d*$/.test(value)) return false;
    const num = parseFloat(value);
    // NaN means partial input like '' or '.': don't block while typing.
    return isNaN(num) || num <= max;
  };

  //handle input change event
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    parameterId: number
  ) => {
    const { value, name } = e.target;
    const max = getOffsetMax(name);

    if (!isValidOffsetInput(value, max)) {
      // If the only reason it failed is "value exceeds the cap" (format/length
      // are still fine), flash a transient warning so the user knows why their
      // keystroke was rejected.
      if (value.length <= 10 && /^\d*\.?\d*$/.test(value)) {
        const num = parseFloat(value);
        if (!isNaN(num) && num > max) {
          flashMaxWarning(name as 'offsetX' | 'offsetY');
        }
      }
      // Ignore the change — keeps the previous valid value in state.
      return;
    }

    if (name == 'offsetX') {
      setOffsetsData(prev =>
        prev.map(p =>
          p.parameterId == parameterId ? { ...p, offsetX: value } : p
        )
      );
    } else {
      setOffsetsData(prev =>
        prev.map(p =>
          p.parameterId == parameterId ? { ...p, offsetY: value } : p
        )
      );
    }

    // Clear the error for this field once the user resumes typing a valid value.
    if (isCompleteValidNumber(value, max)) {
      setOffsetErrors(prev => {
        const row = prev[parameterId];
        if (!row) return prev;
        const next = { ...row };
        delete next[name as 'offsetX' | 'offsetY'];
        const updated = { ...prev };
        if (Object.keys(next).length === 0) delete updated[parameterId];
        else updated[parameterId] = next;
        return updated;
      });
    }
  };

  // Validate a single field on blur — flags bare "." or other incomplete entries.
  const handleBlur = (e: ChangeEvent<HTMLInputElement>, parameterId: number) => {
    const { name, value } = e.target;
    const valid = isCompleteValidNumber(value, getOffsetMax(name));
    setOffsetErrors(prev => {
      const row = { ...(prev[parameterId] || {}) };
      if (valid) delete row[name as 'offsetX' | 'offsetY'];
      else row[name as 'offsetX' | 'offsetY'] = true;
      const updated = { ...prev };
      if (Object.keys(row).length === 0) delete updated[parameterId];
      else updated[parameterId] = row;
      return updated;
    });
  };

  const hasAnyOffsetError = Object.keys(offsetErrors).length > 0;

  if (!isOpen) return null;
  return (
    <div className="modal-overlay zoomOutLevel">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <div className="flex md:items-start items-center md:flex-col flex-row">
            <button onClick={onClose} className="md:hidden block mr-3">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h2 className="modal-title">Update Offsets</h2>
            <p className="modal-subtitle md:block hidden">
              Adjust the offset values to keep your data accurate.
            </p>
          </div>
          <button onClick={onClose} className="modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form className="modal-form">
          <div className="device-offsets-container">
            <div className="device-offsets-header">
              <p>Parameter</p>
              <p>X-Offset</p>
              <p>Y-Offset</p>
            </div>
            <div className="device-offsets-body">
              {offsetsData.map((data, index) => (
                <div key={index} className="device-offsets-row">
                  <p>
                    {data.parameterName == 'Temperature'
                      ? 'Temp'
                      : data.parameterName}
                  </p>
                  <input
                    type="text"
                    name="offsetX"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    maxLength={10}
                    value={data.offsetX}
                    onChange={e => handleInputChange(e, data.parameterId)}
                    onBlur={e => handleBlur(e, data.parameterId)}
                    className={`device-offsets-input ${
                      offsetErrors[data.parameterId]?.offsetX
                        ? 'device-offsets-input-error'
                        : ''
                    }`}
                  />
                  <input
                    type="text"
                    name="offsetY"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    maxLength={10}
                    value={data.offsetY}
                    onChange={e => handleInputChange(e, data.parameterId)}
                    onBlur={e => handleBlur(e, data.parameterId)}
                    className={`device-offsets-input ${
                      offsetErrors[data.parameterId]?.offsetY
                        ? 'device-offsets-input-error'
                        : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        </form>
        {(maxWarning.offsetX || maxWarning.offsetY) && (
          <p className="device-offsets-error-message">
            {maxWarning.offsetX && `X-Offset cannot exceed ${OFFSET_X_MAX}.`}
            {maxWarning.offsetX && maxWarning.offsetY ? ' ' : ''}
            {maxWarning.offsetY && `Y-Offset cannot exceed ${OFFSET_Y_MAX}.`}
          </p>
        )}
        {hasAnyOffsetError && (
          <p className="device-offsets-error-message">
            Please enter a valid number for the highlighted offsets (X-Offset: 0–{OFFSET_X_MAX}, Y-Offset: 0–{OFFSET_Y_MAX}).
          </p>
        )}
        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="modal-button secondary"
          >
            Cancel
          </button>
          <button onClick={handleSubmit} className="modal-button primary">
            Update
          </button>
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title={successMessage}
        subtitle={successAction}
        status={successStatus}
        actionText={undefined}
        onAction={undefined}
      />
    </div>
  );
}

export default DeviceOffSets;
